import React from 'react';
import { ConnectionTestResult, ConnectionTestStage, StageResult, ConnectionType } from '../../types/connection';
import { StatusIndicator, StatusType } from '../shared/StatusIndicators';
// THUB-243 owns the failure WORDING: it is transport-aware, so an Azure Blob failure is not
// told to check SSH keys, and it prefers Azure's own error code over our flattened AUTH_FAILED.
// It supersedes THUB-138's flat errorCode->string map.
import { getConnectionTestGuidance } from '../../lib/connectionTestGuidance';
// THUB-186: timezone-aware shared formatter (was new Date(x).toLocaleString()).
import { formatDateTime, activeTimezone } from '../../utils/formatDateTime';
// THUB-138 keeps the orthogonal part: WHETHER to show the firewall-allowlist hint, gated to
// genuine reachability failures so we don't tell an operator to chase a partner firewall for
// an auth or host-key problem. That gating is not part of THUB-243's wording.
import {
  FIREWALL_PANEL_LABEL,
  shouldShowAllowlistHint,
} from '../../lib/connectionTestMessages';

interface TestResultPanelProps {
  result: ConnectionTestResult;
  showStages?: boolean;
  compact?: boolean;
  connectionType?: ConnectionType;
  transferMethod?: string;
}

// Map stages to display names for SFTP outbound connections
const OUTBOUND_STAGE_NAMES: Record<ConnectionTestStage, string> = {
  network: 'Network Connectivity',
  authentication: 'Authentication',
  directory: 'Directory Access',
  unknown: 'Unknown Stage'
};

// Map stages to display names for SFTP inbound connections
const INBOUND_STAGE_NAMES: Record<ConnectionTestStage, string> = {
  network: 'Public Key Validation',
  authentication: 'IP Range Configuration',
  directory: 'Transfer Family User',
  unknown: 'Unknown Check'
};

// Map stages to display names for HTTP/API connections
const HTTP_STAGE_NAMES: Record<ConnectionTestStage, string> = {
  network: 'Endpoint Reachability',
  authentication: 'Authentication',
  directory: 'API Response',
  unknown: 'Unknown Stage'
};

// THUB-243/245: Azure Blob stages. The probe reaches the blob endpoint, authorizes the SAS, then
// exercises whatever the SAS actually permits (List if 'l', Get Blob Properties if 'r', a staged
// uncommitted Put Block if 'w'/'c'/'a'). "Directory Access" is not an Azure concept -- without
// this map Azure fell through to the SFTP outbound labels.
const AZURE_STAGE_NAMES: Record<ConnectionTestStage, string> = {
  network: 'Blob Endpoint Reachability',
  authentication: 'SAS Authorization',
  directory: 'Container Access',
  unknown: 'Unknown Stage'
};

function StageResultItem({ stage, stageName, result }: { stage: ConnectionTestStage; stageName: string; result?: StageResult }) {
  if (!result) {
    return (
      <div className="flex items-center gap-3 px-3 py-2.5 rounded-md bg-neutral-100 dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 opacity-70">
        <span className="w-6 h-6 flex items-center justify-center rounded-full bg-neutral-200 dark:bg-neutral-700 text-neutral-500 dark:text-neutral-400 text-xs font-bold">
          -
        </span>
        <span className="flex-1 text-sm font-medium text-neutral-600 dark:text-neutral-400">{stageName}</span>
        <span className="text-xs text-neutral-500 dark:text-neutral-400">Not tested</span>
      </div>
    );
  }

  const baseClasses = "flex items-center gap-3 px-3 py-2.5 rounded-md border";
  const stateClasses = result.success
    ? "bg-success-500/5 dark:bg-success-500/10 border-success-500/15 dark:border-success-500/20"
    : "bg-danger-500/5 dark:bg-danger-500/10 border-danger-500/15 dark:border-danger-500/20";
  const iconClasses = result.success
    ? "bg-success-500/15 dark:bg-success-500/20 text-success-600 dark:text-success-400"
    : "bg-danger-500/15 dark:bg-danger-500/20 text-danger-600 dark:text-danger-400";

  return (
    <div className={`${baseClasses} ${stateClasses}`}>
      <span className={`w-6 h-6 flex items-center justify-center rounded-full text-xs font-bold ${iconClasses}`}>
        {result.success ? 'OK' : 'X'}
      </span>
      <span className="flex-1 text-sm font-medium text-neutral-800 dark:text-neutral-200">{stageName}</span>
      {result.duration !== undefined && (
        <span className="text-xs text-neutral-500 dark:text-neutral-400">{Math.round(result.duration)}ms</span>
      )}
      {result.error && (
        <span
          className="text-xs text-danger-600 dark:text-danger-400 max-w-[300px] overflow-hidden text-ellipsis whitespace-nowrap"
          title={result.error}
        >
          {result.error.substring(0, 60)}{result.error.length > 60 ? '...' : ''}
        </span>
      )}
    </div>
  );
}

export function TestResultPanel({ result, showStages = true, compact = false, connectionType = 'outbound', transferMethod }: TestResultPanelProps) {
  // Select stage names based on connection type and transfer method
  const STAGE_NAMES = transferMethod === 'azure_blob' ? AZURE_STAGE_NAMES
    : transferMethod === 'api' ? HTTP_STAGE_NAMES
    : connectionType === 'inbound' ? INBOUND_STAGE_NAMES : OUTBOUND_STAGE_NAMES;
  const isInbound = connectionType === 'inbound';

  const getStatusType = (): StatusType => {
    return result.success ? 'healthy' : 'error';
  };

  const getErrorMessage = (): string | null => {
    if (result.success) return null;
    // THUB-243: transport-aware guidance. The old map was keyed on errorCode alone, so an Azure
    // failure was told to check "SSH key configuration". It also prefers Azure's own error code
    // (AuthorizationFailure = firewall, not credentials) over our flattened AUTH_FAILED.
    return (
      getConnectionTestGuidance(result.errorCode, result.error, transferMethod) ||
      'Connection test failed'
    );
  };

  const getFailedStageName = (): string | null => {
    if (result.success || !result.errorStage) return null;
    return STAGE_NAMES[result.errorStage] || result.errorStage;
  };

  const errorMessage = getErrorMessage();
  const failedStage = getFailedStageName();
  // THUB-138: only a genuine reachability failure gets the partner-firewall callout. The IPs
  // themselves are NOT rendered here -- the existing "Firewall Whitelist IPs" panel on the
  // Overview tab already renders connection.connectorEgressIps, and duplicating that logic
  // would let the two disagree.
  const showAllowlistHint = !result.success && shouldShowAllowlistHint(result.errorCode);

  const panelBaseClasses = "p-4 rounded-lg mb-4 border";
  const panelStateClasses = result.success
    ? "bg-success-500/10 dark:bg-success-500/15 border-success-500/20 dark:border-success-500/25"
    : "bg-danger-500/10 dark:bg-danger-500/15 border-danger-500/20 dark:border-danger-500/25";

  if (compact) {
    return (
      <div className={`flex items-center gap-3 px-3 py-2 rounded-lg ${panelStateClasses}`}>
        <StatusIndicator
          status={getStatusType()}
          label={result.success ? 'Success' : 'Failed'}
          size="small"
        />
        {result.responseTime !== undefined && (
          <span className="text-xs text-neutral-500 dark:text-neutral-400">{Math.round(result.responseTime)}ms</span>
        )}
        {!result.success && errorMessage && (
          <span
            className="text-xs text-danger-600 dark:text-danger-400 flex-1 overflow-hidden text-ellipsis whitespace-nowrap"
            title={errorMessage}
          >
            {failedStage ? `Failed at ${failedStage}` : 'Test failed'}
          </span>
        )}
      </div>
    );
  }

  return (
    <div className={`${panelBaseClasses} ${panelStateClasses}`}>
      <div className="flex items-center justify-between mb-3">
        <StatusIndicator
          status={getStatusType()}
          label={result.success ? 'Connection Test Passed' : 'Connection Test Failed'}
        />
        {result.responseTime !== undefined && (
          <span className="text-sm text-neutral-500 dark:text-neutral-400">
            Total time: {Math.round(result.responseTime)}ms
          </span>
        )}
      </div>

      {result.testedAt && (
        <div className="text-xs text-neutral-500 dark:text-neutral-400 mb-3">
          Tested: {formatDateTime(result.testedAt, activeTimezone(), 'full')}
        </div>
      )}

      {!result.success && errorMessage && (
        <div className="bg-danger-500/5 dark:bg-danger-500/10 border border-danger-500/15 dark:border-danger-500/20 rounded-md p-3 mb-4">
          <div className="flex items-center gap-2 mb-2">
            <strong className="text-danger-600 dark:text-danger-400">Error:</strong>
            {failedStage && (
              <span className="text-xs text-neutral-600 dark:text-neutral-400 bg-neutral-200/50 dark:bg-neutral-700/50 px-1.5 py-0.5 rounded">
                at {failedStage}
              </span>
            )}
          </div>
          <p className="text-sm text-neutral-800 dark:text-neutral-200 leading-relaxed">{errorMessage}</p>
          {showAllowlistHint && (
            <div
              className="
                mt-3 p-2.5 rounded-md
                bg-amber-500/10 dark:bg-amber-500/15
                border border-amber-500/25 dark:border-amber-500/30
              "
              data-testid="allowlist-hint"
            >
              <p className="text-xs text-amber-800 dark:text-amber-200 leading-relaxed">
                <strong className="font-semibold">Partner firewall:</strong>{' '}
                TransferHub connects from a fixed set of outbound IP addresses. If the partner
                has not allowlisted them, connections fail exactly like this. The addresses for
                this connection are listed in the <strong>{FIREWALL_PANEL_LABEL}</strong>{' '}
                section on the Overview tab -- send those to the partner.
              </p>
            </div>
          )}
          {result.error && result.error !== errorMessage && (
            <details className="mt-3">
              <summary className="text-xs text-neutral-500 dark:text-neutral-400 cursor-pointer select-none hover:text-neutral-700 dark:hover:text-neutral-300">
                Technical Details
              </summary>
              <pre className="mt-2 p-2 bg-neutral-100/50 dark:bg-neutral-900/50 rounded text-xs overflow-x-auto whitespace-pre-wrap break-all text-neutral-700 dark:text-neutral-300">
                {result.error}
              </pre>
            </details>
          )}
        </div>
      )}

      {showStages && result.stages && (
        <div className="mt-4">
          <h4 className="text-sm font-semibold text-neutral-800 dark:text-neutral-200 mb-3">
            {isInbound ? 'Configuration Checklist' : 'Test Stages'}
          </h4>
          <div className="flex flex-col gap-2">
            <StageResultItem
              stage="network"
              stageName={STAGE_NAMES.network}
              result={result.stages.hostReachable}
            />
            <StageResultItem
              stage="authentication"
              stageName={STAGE_NAMES.authentication}
              result={result.stages.authentication}
            />
            <StageResultItem
              stage="directory"
              stageName={STAGE_NAMES.directory}
              result={result.stages.directoryListing}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default TestResultPanel;
